// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionDetail.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import EventsUtils from 'EventUtils';
import { isEmpty, findLast, find, getOr, isNil } from 'lodash/fp';
import { action, observable } from 'mobx';

import Panel from 'share/components/controls/Izenda.Panel';
import Label from 'share/components/controls/Izenda.Label';
import Span from 'share/components/controls/Izenda.Span';
import FormGroup from 'share/components/controls/Izenda.FormGroup';
import Icon from 'share/components/controls/Izenda.Icon';
import Button from 'share/components/controls/Izenda.Button';
import ConnectionStringBuilder from
    'share/components/controls/connectionStringBuilder/Izenda.ConnectionStringBuilder';
import ConnectionStringBuilderRest from
    'share/components/controls/ConnectionStringBuilderRest/Izenda.ConnectionStringBuilderRest';
import constants from 'logic/data/Izenda.ConnectionConstants';
import Input from 'share/components/controls/Izenda.Input';
import Validator from 'share/components/hoc/Izenda.Validator';
import FileUploadPopup from 'share/components/controls/FileUploadPopup/Izenda.FileUploadPopup';
import ConnectionDetailStore from 'logic/Izenda.ConnectionDetailStore';
import ConnectionUtils from 'modules/Settings/DataSetup/Connection/Izenda.ConnectionUtils';

const ValidatableInput = Validator()(Input);

/**
 * ConnectionDetail component
 */
@connect()
export default class ConnectionDetail extends Component {
  static propTypes = {
    connectionDetailStore: React.PropTypes.object.isRequired, // ConnectionDetailStore
    item: React.PropTypes.object.isRequired,
    dbTypeList: React.PropTypes.object.isRequired
  };

  @observable
  fileUploadError = null;

  endpointModels = [];

  componentWillReceiveProps(nextProps) {
    const { props } = this;
    const oldId = getOr(null, 'item.id', props);
    const newId = getOr(null, 'item.id', nextProps);
    // Only load data when showing different connection.
    if (newId && oldId !== newId) {
      const { loader } = props;
      const connectionDetailStore: ConnectionDetailStore = nextProps.connectionDetailStore;
      loader.startLoading();
      return connectionDetailStore
        .load(newId)
        .then(() => true)
        .finally(() => loader.endLoading());
    }
  }

  /**
   * Checks whether current connection detail belongs to given db type or not.
   * @param {string} dbTypeShortName
   * @param {ConnectionDetail} connectionDetail
   * @returns {boolean}
   */
  isDbType(dbTypeShortName, connectionDetail) {
    const { props } = this;
    const connectionDetailStore: ConnectionDetailStore = props.connectionDetailStore;
    return ConnectionUtils.isDbType(connectionDetail.serverTypeId, dbTypeShortName, connectionDetailStore.masterStore);
  }

  /**
   * Check is Excel selected or not.
   */
  get isFileUploadRequired(): boolean {
    const { item } = this.props;
    return this.isDbType('EXCEL', item);
  }

  /**
   * Check is Rest selected or not.
   */
  get isRestApi(): boolean {
    const { item } = this.props;
    return this.isDbType('REST', item);
  }


  renderConnectorName() {
    const { item, localization } = this.props;
    const disabled = !item.visible;
    const { notifyChange } = EventsUtils;

    return (
      <FormGroup className="u_fullWidth">
        <Label className="validation required">{localization.getText('CONNECTION_LABEL_DATABASE_NAME')}</Label>
        <ValidatableInput
          field="name"
          validations={constants.DATABASE_NAME_CONSTRAINT}
          className="form-control u_fullWidth"
          onChange={notifyChange(item.setName.bind(item), EventsUtils.INPUT)}
          value={item.name}
          disabled={disabled}
        />
      </FormGroup>);
  }

  renderConnectorType() {
    const { item, dbTypeList, localization } = this.props;
    const dbType = find({id: item.serverTypeId}, dbTypeList);

    const shortName = getOr(null, 'shortName', dbType);
    const iconClasses = classnames(
      'izenda-Connection-dbTypeIcon medium u_noPaddingAll u_block',
      'u_marginAutoLeftRight u_noMarginTop', {
      [shortName]: !isNil(shortName)
    });

    return (
      <div className="izenda-Connection-dbTypIconOuter">
        <div className={iconClasses}/>
        <div>{dbType && dbType.name && dbType.name.substring(dbType.name.indexOf(']') + 2)}</div>
      </div>
    );
  }

  /**
   * "Edit connection"/"Upload Excel file" button
   */
  renderConnectionStringBuilderButton() {
    const { item, localization } = this.props;
    const disabled = !item.visible;
    const buttonText = localization.getText(
      this.isFileUploadRequired
        ? 'CONNECTION_LABEL_CONNECTION_BUILDER_UPLOAD'
        : 'CONNECTION_LABEL_CONNECTION_BUILDER'
    );

    return (
      <Button
        disabled={disabled}
        className="u_inlineBlock u_floatRight"
        onClick={this.showConnectionStringBuilder}>
        <Span className="no-padding-left-right">
          {buttonText}
        </Span>
        <Icon className="icon-flash"/>
      </Button>
    );
  }

  renderConnectButton() {
    const { item } = this.props;
    const disabledConnect = !item.isConnectionStringReady;

    return (
      <Button
        disabled={disabledConnect}
        className="u_inlineBlock u_floatRight btn-default u_marginLeft"
        onClick={this.props.onConnectToDataSource}>
        <Span className="no-padding-left-right">
          {constants.CONNECT_TYPE.RECONNECT}
        </Span>
        <Icon className="icon-flash"/>
      </Button>
    );
  }

  render() {
    const { item } = this.props;
    if (isEmpty(item.dbSource)) {
      return null;
    }

    return (
      <Panel className="connection-string-validation-container">
        <div className="row">

          {/*Connector type dropdown*/}
          <div className="u_marginRight u_floatLeft">
            {this.renderConnectorType()}
          </div>

          {/*Connector name input*/}
          <div className="connector-name u_floatLeft">
            {this.renderConnectorName()}
          </div>

          <div className="connector-buttons">
            {this.renderConnectButton()}
            {this.renderConnectionStringBuilderButton()}
          </div>
        </div>
      </Panel>
    );
  }

  /**
   * File uploaded handler
   * @param content result
   */
  onFileUploadSuccess = (content) => {
    const { success } = content;
    if (!success) {
      const messageText = getOr(null, 'messages[0].messages[0]', content);
      this.onFileUploadError(messageText);
      return;
    }
    const { data: { connectionString } } = content;
    this.applyConnectionString(connectionString);
  };

  /**
   * Reset file upload error message
   */
  @action
  resetFileUploadError = () => this.fileUploadError = null;

  /**
   * File uploaded fail handler
   * @param error
   */
  @action
  onFileUploadError = (error) => {
    this.fileUploadError = error;
    this.onResetConnectionString();
  };

  onValidated = (success) => {
    this.props.item.isConnectionStringValid = success;
  };

  /**
   * [onResetConnectionString function]
   * is to reset connection string validation value
   */
  onResetConnectionString = () => {
    const detail = this.props.item;
    if (detail.isSecured) {
      this.props.validation.reset('connectionString');
    }
  };

  applyConnectionString = (connectionString) => {
    const { connectionDetailStore: { item } } = this.props;
    item.setConnectionString(connectionString);
  };

  applyConnectionStringRest = ({connectionString, endpointModels}) => {
    this.applyConnectionString(connectionString);
    this.endpointModels = endpointModels;
  };

  /**
   * Display Connection string builder popup that allows user to enter the
   * information of a connection string.
   * @param  {Object} e React-event object
   */
  showConnectionStringBuilder = e => {
    e.preventDefault();
    this.resetFileUploadError();
    const { props } = this;
    if (!props.item.visible === true) {
      return;
    }

    const { connectionDetailStore, dbTypeList, item, localization } = props;

    // There is 3 possible reactions on connection builder button:
    // - open file uploading popup
    // - open rest api connection string builder
    // - open connection string builder popup
    if (this.isFileUploadRequired) {
      // Upload file
      const params = {
        batchId: String.newGuid(),
        connectorId: getOr(constants.EMPTY_GUID, 'serverTypeId', item), // send current serverTypeId
        connectionId: getOr(constants.EMPTY_GUID, 'id', item)
      };
      const openFileUploadPopup = () => props.openPopup(FileUploadPopup, {
        fileExtentions: constants.EXCEL_FILE_EXTENSIONS,
        url: connectionDetailStore.getUploadUrl(),
        params: params,
        onSuccess: this.onFileUploadSuccess,
        onError: this.onFileUploadError,
      }).then(() => {
        connectionDetailStore.masterStore.addTempExcelFileToDeleteQueue(item.connectionString);

        // Show error message.
        if (this.fileUploadError) {
          props.confirmMessage('error', this.fileUploadError);
        } else {
          connectionDetailStore
            .performConnection(item.connectionString).then(() => {});
        }
        return null;
      });

      if (item.isNew)
        return openFileUploadPopup();
      else
        return props.confirmFn('Confirmation', {
          description: constants.OVERRIDE_EXCEL_MESSAGE,
          confirmLabel: localization.getText('COMMON_LABEL_YES'),
          abortLabel: localization.getText('COMMON_LABEL_NO'),
        }).then(openFileUploadPopup);

    } else if (this.isRestApi) {
      // Rest connection string
      return props
        .openPopup(ConnectionStringBuilderRest, {
          connectionDetailStore: connectionDetailStore,
          endpointModels: this.endpointModels
        })
        .then(({ connectionString, endpointModels }) => {
          item.resetModelError();
          item.endpointModels = endpointModels;
          this.applyConnectionStringRest({
            connectionString: connectionString,
            endpointModels: endpointModels
          });
        });
    } else {
      // Connection string
      const serverTypeId = item.serverTypeId;
      const serverTypeName = findLast({ id: serverTypeId }, dbTypeList).name;
      return props.openPopup(ConnectionStringBuilder, {
        onOk: () => {},
        serverType: serverTypeName,
        connectionDetailStore: connectionDetailStore,
        isConnectionStringEnabled: true
      });
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Connection/ConnectionConfig/ConnectionDetail/Izenda.ConnectionDetail.js